import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { PieChartInternal } from './components/PieChartInternal.js';
import {
  CHART_VARIANT,
  DONUT_CHART_ARIA_LABEL,
  DONUT_CHART_DISPLAY_NAME,
} from './constants.js';
import { InnerElementContext } from './context/InnerElementContext.js';
import { useNormalizedPieData } from './hooks/useNormalizedPieData.js';
import { usePieChartConfig } from './hooks/usePieChartConfig.js';
import {
  ChartProviders,
  type ChartProvidersProps,
} from './providers/ChartProviders.js';
import { Toolbar } from './shared/slots/Toolbar.js';
import { DonutChartDownloadCSVSlot } from './slots/DonutChartDownloadCSV.js';
import { Grouping as DonutChartGrouping } from './slots/DonutChartGrouping.js';
import { DonutChartInner } from './slots/DonutChartInner.js';
import { Labels as DonutChartLabels } from './slots/DonutChartLabels.js';
import { Legend as DonutChartLegend } from './slots/DonutChartLegend.js';
import { Tooltip as DonutChartTooltipSlot } from './slots/DonutChartTooltip.js';
import type { DonutChartRef, PieChartRef } from './types/pie-chart-ref.js';
import type { DonutChartProps } from './types/pie-chart.config.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { sanitizeToolbarIntents } from './utils/sanitize-toolbar-intents.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { ChartType } from '../core/types/chart-types.js';

const COMPONENT_NAME = 'DonutChart';

const DonutChartRoot = forwardRef<
  DonutChartRef,
  PropsWithChildren<DonutChartProps>
>((props, forwardedRef) => {
  const {
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'donut-chart-root',
  } = props;

  const chartConfig = usePieChartConfig(props);
  const { emptyState, errorState } = getChartStatesTemplates(props.children);

  const { normalizedData, isDataAvailable } = useNormalizedPieData(props);

  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<PieChartRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  useImperativeHandle(
    forwardedRef,
    (): DonutChartRef => ({
      element: containerRef.current,
      getConfig: () =>
        JSON.stringify({
          type: ChartType.Donut,
          ...sanitizeToolbarIntents(chartConfig),
        }),
      downloadData: () => {
        buildAndDownloadCsv(normalizedData);
      },
    }),
  );

  const providersProps: ChartProvidersProps = {
    chartData: normalizedData,
    chartConfig,
  };

  return (
    <InnerElementContext.Provider
      value={{
        innerElement: chartConfig.innerElement,
      }}
    >
      <ChartProviders {...providersProps}>
        <PieChartInternal
          {...props}
          ref={rootRef}
          variant={CHART_VARIANT.Donut}
          isEmpty={!isDataAvailable}
          emptyState={emptyState}
          errorState={errorState}
          className={clsx(defaultChartSize, className)}
          style={style}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          data-testid={dataTestId}
          data-dt-component={COMPONENT_NAME}
          aria-label={DONUT_CHART_ARIA_LABEL}
          role="img"
        />
      </ChartProviders>
    </InnerElementContext.Provider>
  );
});

(
  DonutChartRoot as typeof DonutChartRoot & { displayName: string }
).displayName = DONUT_CHART_DISPLAY_NAME;

/**
 * The `DonutChart` is a variation of a pie chart with a circular shape and
 * a hole in the center. It divides the total amount into proportional slices,
 * each representing a category or value. The key difference is the presence of
 * the central hole, which allows placing additional information in the
 * available space.
 * @public
 */
export const DonutChart = Object.assign(DonutChartRoot, {
  Legend: DonutChartLegend,
  Grouping: DonutChartGrouping,
  Labels: DonutChartLabels,
  Inner: DonutChartInner,
  Tooltip: DonutChartTooltipSlot,
  Intent,
  Toolbar,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  ColorRule,
  DownloadCSV: DonutChartDownloadCSVSlot,
});
